import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Container,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTeacherAuth } from "../auth/TeacherAuthContext";

const LearnerCard = React.memo(({ learner, marks, handleMarkChange }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0, transition: { duration: 0.5 } }}
      whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
    >
      <Card
        sx={{ p: 2, borderRadius: "10px", borderLeft: "4px solid #6200ee" }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <PersonIcon sx={{ color: "#6200ee", mr: 1 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: "600", color: "#2c3e50" }}
            >
              {learner.fullName}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
            Adm: {learner.admissionNumber}
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            label="Marks (0-100)"
            value={marks[learner._id] || ""}
            onChange={(e) => handleMarkChange(learner._id, e.target.value)}
            type="number"
            inputProps={{ min: 0, max: 100 }}
          />
        </CardContent>
      </Card>
    </motion.div>
  );
});

const UpdateClassResults = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { teacherData } = useTeacherAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fallback to teacherData if state is missing
  const schoolId = state?.schoolId || teacherData?.schoolId;
  const teacherId = state?.teacherId || teacherData?.teacherId;
  const scheduledExam = state?.scheduledExam || null;

  const [className, setClassName] = useState(scheduledExam?.className || "");
  const [subject, setSubject] = useState(scheduledExam?.subject || "");
  const [stream, setStream] = useState(scheduledExam?.stream || "");
  const [term, setTerm] = useState(scheduledExam?.term || "Term 1");
  const [examType, setExamType] = useState(
    scheduledExam?.examType || "Opener Exam"
  );
  const [examName, setExamName] = useState(scheduledExam?.examName || "");
  const [learners, setLearners] = useState([]);
  const [allLearners, setAllLearners] = useState([]);
  const [marks, setMarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchingLearners, setFetchingLearners] = useState(false);
  const [schoolStreams, setSchoolStreams] = useState({});
  const [fetchingStreams, setFetchingStreams] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("admissionNumber");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Fetch streams when className changes
  useEffect(() => {
    const fetchStreams = async () => {
      if (!schoolId || !className) return;
      setFetchingStreams(true);
      try {
        const response = await axios.get(
          `https://ed-9ny1.onrender.com/api/schools/${schoolId}/streams`
        );
        setSchoolStreams(response.data.classStreams || {});
        if (!response.data.classStreams[className]?.length) {
          setStream("No Stream");
        }
      } catch (err) {
        console.error("Error fetching streams:", err);
        setError("Failed to load streams");
        setSchoolStreams({});
      } finally {
        setFetchingStreams(false);
      }
    };
    fetchStreams();
  }, [schoolId, className]);

  // Fetch all learners when className changes
  useEffect(() => {
    const fetchAllLearners = async () => {
      if (!className || !schoolId) return;

      setFetchingLearners(true);
      try {
        const response = await axios.get(
          "https://ed-9ny1.onrender.com/api/schools/learners",
          { params: { schoolId, className } }
        );
        setAllLearners(response.data || []);
      } catch (err) {
        console.error("Error fetching learners:", err);
        setError("Failed to fetch learners");
        setAllLearners([]);
      } finally {
        setFetchingLearners(false);
      }
    };
    fetchAllLearners();
  }, [className, schoolId]);

  // Filter learners based on stream and search
  useEffect(() => {
    if (!stream || allLearners.length === 0) {
      setLearners([]);
      return;
    }

    const hasStreams =
      schoolStreams[className] && schoolStreams[className].length > 0;
    let filteredLearners = allLearners.filter(
      (learner) => learner.class === className
    );

    if (hasStreams) {
      if (stream === "No Stream") {
        filteredLearners = filteredLearners.filter(
          (learner) => !learner.stream || learner.stream.trim() === ""
        );
      } else {
        filteredLearners = filteredLearners.filter(
          (learner) => learner.stream === stream
        );
      }
    }

    // Apply search filter
    if (debouncedSearchQuery) {
      filteredLearners = filteredLearners.filter(
        (learner) =>
          learner.fullName
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          learner.admissionNumber
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase())
      );
    }

    // Apply sorting
    filteredLearners.sort((a, b) => {
      if (sortBy === "fullName") {
        return a.fullName.localeCompare(b.fullName);
      }
      return a.admissionNumber.localeCompare(b.admissionNumber);
    });

    setLearners(filteredLearners);
  }, [
    stream,
    allLearners,
    className,
    schoolStreams,
    debouncedSearchQuery,
    sortBy,
  ]);

  const getClasses = useCallback(
    () => [
      "PG",
      "PP1",
      "PP2",
      "Grade 1",
      "Grade 2",
      "Grade 3",
      "Grade 4",
      "Grade 5",
      "Grade 6",
      "Grade 7",
      "Grade 8",
      "Grade 9",
      "Grade 10",
      "Grade 11",
      "Grade 12",
    ],
    []
  );

  const getSubjectsByClass = useCallback((className) => {

    if (
      className.includes('PG') 
    ) {
      return [
        'Number Work',
        'Language/Total',
        'Reading/Jumla',
        'Creative',
        'Science and Environment',
        'Social Studies and Religious Education',
        
      ];
    }
    else if (
      className.includes('PP1') ||
      className.includes('PP2') 
    ) {
      return [
        'Number Work',
        'Language/Total',
        'Kiswahili/Jumla',
        'Creative Arts',
        'Science and Environment',
        'Social Studies and Religious Education',
        
      ];
    }
      else if (
      ["Grade 1", "Grade 2", "Grade 3"].some((grade) =>
        className.includes(grade)
      )
    ) {
      return [
        "Language",
        "Mathematics",
        "Psychomotor and Creative Arts",
        "Environmental",
        "Religious Education",
      ];
    } else if (
      ["Grade 4", "Grade 5", "Grade 6"].some((grade) =>
        className.includes(grade)
      )
    ) {
      return [
        "English Language",
        "English Composition",
        "Kiswahili Lugha",
        "Kiswahili Insha",
        "Mathematics",
        "Science and Technology",
        "Agriculture and Nutrition",
        "Social Studies",
        "Religious Education",
        "Creative Arts & Sports",
      ];
    } else if (
      ["Grade 7", "Grade 8", "Grade 9"].some((grade) =>
        className.includes(grade)
      )
    ) {
      return [
        "English Language",
        "English Composition",
        "Kiswahili Lugha",
        "Kiswahili Insha",
        "Mathematics",
        "Science and Technology",
        "Agriculture and Nutrition",
        "Pre-Technical Studies",
        "Social Studies",
        "Religious Education",
        "Creative Arts & Sports",
      ];
    } else if (
      ["Grade 10", "Grade 11", "Grade 12"].some((grade) =>
        className.includes(grade)
      )
    ) {
      return [
        "English",
        "Kiswahili Language/Kenya Sign Language",
        "Mathematics",
        "Religious Education",
        "Science and Technology",
        "Agriculture and Nutrition",
        "Biology",
        "Chemistry",
        "Physics",
        "History and Government",
        "Geography",
        "Business Studies",
        "Computer Studies",
        "Home Science",
        "Art and Design",
        "Agriculture",
        "Music",
        "French",
        "German",
        "Arabic",
      ];
    }
    return [];
  }, []);

  const getTerms = useCallback(() => ["Term 1", "Term 2", "Term 3"], []);
  const getExamTypes = useCallback(
    () => [
      "Opener Exam",
      "Midterm Exam",
      "Endterm Exam",
      "Mock Exam",
      "Random Exams",
      "Other Exams",
    ],
    []
  );

  const handleMarkChange = useCallback((learnerId, value) => {
    const numValue = value.replace(/[^0-9]/g, "");
    if (
      numValue === "" ||
      (parseInt(numValue, 10) >= 0 && parseInt(numValue, 10) <= 100)
    ) {
      setMarks((prev) => ({
        ...prev,
        [learnerId]: numValue,
      }));
    }
  }, []);

  const validateInputs = useCallback(() => {
    if (!schoolId || !teacherId) return "Missing school or teacher information";
    if (!className) return "Please select a class";
    if (!subject) return "Please select a subject";
    if (!stream) return "Please select a stream";
    if (!term) return "Please select a term";
    if (!examType) return "Please select an exam type";
    if (
      (examType === "Random Exams" || examType === "Other Exams") &&
      !examName.trim()
    ) {
      return "Please enter an exam name";
    }
    if (learners.length === 0)
      return "No students found for the selected class/stream";
    const validMarks = Object.values(marks).some(
      (mark) => mark !== "" && !isNaN(mark)
    );
    if (!validMarks) return "Please enter at least one valid mark";
    return null;
  }, [
    schoolId,
    teacherId,
    className,
    subject,
    stream,
    term,
    examType,
    examName,
    learners.length,
    marks,
  ]);

  const handleSubmit = async () => {
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      setSuccess(null);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    const validResults = learners
      .filter(
        (learner) =>
          marks[learner._id] !== "" &&
          marks[learner._id] !== undefined &&
          !isNaN(marks[learner._id])
      )
      .map((learner) => ({
        learnerId: learner._id,
        results: [
          {
            term,
            examType,
            examName: examName || undefined,
            subject,
            marks: parseFloat(marks[learner._id]),
          },
        ],
        teacherId,
        schoolId,
      }));

    try {
      const responses = await Promise.all(
        validResults.map(async (result) => {
          console.log("Sending request for learner:", result.learnerId);
          console.log("Request payload:", result);
          return axios.post(
            "https://ed-9ny1.onrender.com/api/schools/update-results",
            result
          );
        })
      );

      const allSuccessful = responses.every((res) => res.data.success);
      if (allSuccessful) {
        setSuccess("Results updated successfully!");
        setMarks(
          learners.reduce(
            (acc, learner) => ({
              ...acc,
              [learner._id]: "",
            }),
            {}
          )
        );
        navigate(-1);
      } else {
        setError("Some results failed to update");
      }
    } catch (err) {
      console.error("Error updating results:", err);
      setError(err.response?.data?.message || "Failed to update results");
    } finally {
      setLoading(false);
    }
  };

  if (!schoolId || !teacherId) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          bgcolor: "#f5f5f5",
          p: 3,
        }}
      >
        <Alert severity="error">
          Missing school or teacher information. Please log in again.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/teacher/login")}
          sx={{ mt: 2 }}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: "#f8f9fa", minHeight: "100vh", pt: 8 }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }} data-aos="fade-down">
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "#2c3e50", mb: 2 }}
          >
            Update Class Results
          </Typography>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }} data-aos="fade-up">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Class</InputLabel>
              <Select
                value={className}
                onChange={(e) => setClassName(e.target.value)}
                label="Class"
              >
                <MenuItem value="">
                  <em>Select Class</em>
                </MenuItem>
                {getClasses().map((cls) => (
                  <MenuItem key={cls} value={cls}>
                    {cls}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth variant="outlined" disabled={!className}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                label="Subject"
              >
                <MenuItem value="">
                  <em>Select Subject</em>
                </MenuItem>
                {getSubjectsByClass(className).map((sub) => (
                  <MenuItem key={sub} value={sub}>
                    {sub}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl
              fullWidth
              variant="outlined"
              disabled={fetchingStreams || !className}
            >
              <InputLabel>Stream</InputLabel>
              <Select
                value={stream}
                onChange={(e) => setStream(e.target.value)}
                label="Stream"
              >
                <MenuItem value="No Stream">No Stream</MenuItem>
                {schoolStreams[className]?.map((str) => (
                  <MenuItem key={str} value={str}>
                    {str}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl
              fullWidth
              variant="outlined"
              disabled={!!scheduledExam}
            >
              <InputLabel>Term</InputLabel>
              <Select
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                label="Term"
              >
                {getTerms().map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl
              fullWidth
              variant="outlined"
              disabled={!!scheduledExam}
            >
              <InputLabel>Exam Type</InputLabel>
              <Select
                value={examType}
                onChange={(e) => setExamType(e.target.value)}
                label="Exam Type"
              >
                {getExamTypes().map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Exam Name"
              value={examName}
              onChange={(e) => setExamName(e.target.value)}
              disabled={!!scheduledExam}
            />
          </Grid>
        </Grid>

        <Box sx={{ mb: 4 }} data-aos="fade-up">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "#6200ee", mr: 1 }} />,
              }}
            />
            <FormControl variant="outlined" sx={{ minWidth: 150 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="admissionNumber">Admission Number</MenuItem>
                <MenuItem value="fullName">Name</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {fetchingLearners ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress color="primary" />
              <Typography sx={{ mt: 2, color: "#666" }}>
                Loading students...
              </Typography>
            </Box>
          ) : learners.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                bgcolor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <Typography sx={{ color: "#666" }}>
                {stream
                  ? `No students found for ${className}${
                      stream !== "No Stream" ? ` (Stream: ${stream})` : ""
                    }`
                  : "Please select a class and stream"}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {learners.map((learner) => (
                <Grid item xs={12} sm={6} md={4} key={learner._id}>
                  <LearnerCard
                    learner={learner}
                    marks={marks}
                    handleMarkChange={handleMarkChange}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ textAlign: "center" }} data-aos="fade-up">
          <Button
            variant="contained"
            color="primary"
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSubmit}
            disabled={loading || fetchingLearners || fetchingStreams}
            sx={{ px: 4, py: 1.5, borderRadius: "8px" }}
          >
            {loading ? "Submitting..." : "Submit Results"}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default UpdateClassResults;
